import {
  ForwardRefRenderFunction,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useDragScroll} from '../../../common/shared/hooks/useDragScroll';
import {twMerge} from '../../../common/twMerge';
import useStopPropagationOnScroll from '../../../common/useStopPropagationOnScroll';
import {List} from '../../atoms/List/List';
import {ArrowButton} from '../ArrowButton/ArrowButton';
import {
  ArrowButtonDirection,
  ArrowButtonSize,
} from '../ArrowButton/ArrowButton.enum';
import {ScrollableHorizontalListProps} from './ScrollableHorizontalListProps.interface';

const _ScrollableHorizontalList: ForwardRefRenderFunction<
  HTMLUListElement,
  ScrollableHorizontalListProps
> = (
  {
    isMobile,
    arrowButtonSize = ArrowButtonSize.Small,
    isOuterArrow = false,
    gapPx = 0,
    children,
    innerLeftPx = 0,
    innerRightPx = 0,
    blockMouseEvent,
    hideArrow = false,
    className,
    listItemClassName,
    ...props
  },
  forwardRef
) => {
  const newRef = useRef<HTMLUListElement>(null);
  const listRef = (forwardRef as RefObject<HTMLUListElement>) ?? newRef;
  const {isDragScrolling} = useDragScroll(listRef, 5);
  const [shouldShowLeftArrow, setShouldShowLeftArrow] = useState(false);
  const [shouldShowRightArrow, setShouldShowRightArrow] = useState(false);
  const timeoutIdRef = useRef<number | null>(null);

  // scrollable horizontal list의 스크롤 버블링을 막음
  useStopPropagationOnScroll(listRef, blockMouseEvent);

  useEffect(() => {
    if (listRef?.current) {
      const list = listRef?.current;
      // 스크롤이 필요하다면 우측 화살표 버튼을 보여줍니다.
      if (list.scrollWidth > list.offsetWidth) {
        setShouldShowRightArrow(true);
      }
    }
  }, [children, listRef]);

  const onListScroll = useCallback(() => {
    if (listRef.current) {
      const list = listRef.current;

      // 스크롤이 맨 처음으로 돌아가면 좌측 화살표 버튼을 숨깁니다.
      if (list.scrollLeft <= 0) {
        setShouldShowLeftArrow(false);
      }
      // 스크롤이 맨 마지막으로 이동하면 우측 화살표 버튼을 숨깁니다.
      // scrollWidth 값은 기본적으로 반올림 처리되기 때문에, 보다 정확한 비교를 위해 Math.round를 사용합니다.
      else if (
        Math.round(list.scrollLeft + list.offsetWidth) >= list.scrollWidth
      ) {
        setShouldShowRightArrow(false);
      }
      // 두 경우 모두 아니라면 두 화살표를 모두 보여줍니다.
      else {
        setShouldShowLeftArrow(true);
        setShouldShowRightArrow(true);
      }
    }
  }, [listRef]);

  const onClickLeftArrow = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (listRef.current) {
        const list = listRef.current;
        list.scrollBy({
          left: -((list.firstChild as HTMLElement).offsetWidth + gapPx),
          behavior: 'smooth',
        });
      }
    },
    [gapPx, listRef]
  );

  const onClickRightArrow = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (listRef.current) {
        const list = listRef.current;
        list.scrollBy({
          left: (list.firstChild as HTMLElement).offsetWidth + gapPx,
          behavior: 'smooth',
        });
      }
    },
    [gapPx, listRef]
  );

  const stopClickCapturePropagation = useCallback((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    const list = listRef?.current;
    if (list && !isMobile) {
      if (isDragScrolling) {
        list.addEventListener('click', stopClickCapturePropagation, {
          capture: true,
        });
      } else {
        // isScrolling이 false로 변경됐을 때 즉시 이벤트 핸들러를 제거하면 클릭 이벤트를 막을 수 없으므로
        // 50ms의 딜레이를 걸어줍니다.
        timeoutIdRef.current = window.setTimeout(
          () =>
            list.removeEventListener('click', stopClickCapturePropagation, {
              capture: true,
            }),
          50
        );
      }
    }

    return () => {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isDragScrolling, isMobile, listRef, stopClickCapturePropagation]);

  return (
    <div className={twMerge('relative z-0', className)} {...props}>
      <List
        ref={listRef}
        direction="row"
        gapPx={gapPx}
        className="z-0 overflow-y-hidden overflow-x-scroll scrollbar-hide"
        listItemClassName={listItemClassName}
        innerLeftPaddingPx={innerLeftPx}
        innerRightPaddingPx={innerRightPx}
        onScroll={isMobile ? undefined : onListScroll}
      >
        {children}
      </List>

      {!hideArrow && (
        <>
          {!isMobile && shouldShowLeftArrow && (
            <ArrowButton
              direction={ArrowButtonDirection.Left}
              size={arrowButtonSize}
              onClick={onClickLeftArrow}
              className={twMerge(
                'absolute top-1/2 z-1 -translate-y-1/2 will-change-transform',
                isOuterArrow && '-left-22pxr'
              )}
            />
          )}
          {!isMobile && shouldShowRightArrow && (
            <ArrowButton
              direction={ArrowButtonDirection.Right}
              size={arrowButtonSize}
              onClick={onClickRightArrow}
              className={twMerge(
                'absolute top-1/2 z-1 -translate-y-1/2 will-change-transform',
                isOuterArrow && '-right-22pxr'
              )}
            />
          )}
        </>
      )}
    </div>
  );
};

export const ScrollableHorizontalList = forwardRef(_ScrollableHorizontalList);
